import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProducts, updateProduct } from '../api/products';
import { Container, TextField, Button, Typography, Box, Paper, Backdrop, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const UpdateProductForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        price: '',
        features: [],
        image: null
    });
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const response = await getProducts();
                const product = response.data.products.find(p => p._id === id);
                setFormData({
                    title: product.title,
                    description: product.description,
                    price: product.price,
                    features: product.features,
                    image: null
                });
                setPreview(product.image.url);
            } catch (error) {
                console.error('Error fetching product:', error);
            }
        };
        fetchProduct();
    }, [id]);

    const handleChange = (e) => {
        if (e.target.name === 'features') {
            setFormData({
                ...formData,
                features: e.target.value.split(',').map(feature => feature.trim())
            });
        } else {
            setFormData({
                ...formData,
                [e.target.name]: e.target.value
            });
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        setFormData({
            ...formData,
            image: file
        });
        setPreview(URL.createObjectURL(file));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const submitData = new FormData();
        Object.keys(formData).forEach(key => {
            if (key === 'features') {
                submitData.append(key, JSON.stringify(formData[key]));
            } else if (formData[key] !== null) {
                submitData.append(key, formData[key]);
            }
        });

        try {
            await updateProduct(id, submitData);
            navigate('/admin/products');
        } catch (error) {
            console.error('Error updating product:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography variant="h4" gutterBottom>
                        Update Product
                    </Typography>
                    <form onSubmit={handleSubmit}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <TextField
                                name="title"
                                label="Title"
                                value={formData.title}
                                onChange={handleChange}
                                fullWidth
                                required
                            />
                            <TextField
                                name="description"
                                label="Description"
                                value={formData.description}
                                onChange={handleChange}
                                multiline
                                rows={4}
                                fullWidth
                                required
                            />
                            <TextField
                                name="price"
                                label="Price"
                                type="number"
                                value={formData.price}
                                onChange={handleChange}
                                fullWidth
                                required
                            />
                            <TextField
                                name="features"
                                label="Features (comma-separated)"
                                value={formData.features.join(', ')}
                                onChange={handleChange}
                                fullWidth
                            />
                            <Box>
                                <input
                                    accept="image/*"
                                    type="file"
                                    id="image-upload"
                                    onChange={handleImageChange}
                                    style={{ display: 'none' }}
                                />
                                <label htmlFor="image-upload">
                                    <Button
                                        variant="contained"
                                        component="span"
                                        startIcon={<CloudUploadIcon />}
                                    >
                                        Upload New Image
                                    </Button>
                                </label>
                            </Box>
                            {preview && (
                                <Box sx={{ mt: 2 }}>
                                    <img
                                        src={preview}
                                        alt="Preview"
                                        style={{ maxWidth: '100%', maxHeight: '200px' }}
                                    />
                                </Box>
                            )}
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                size="large"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Updating...' : 'Update Product'}
                            </Button>
                        </Box>
                    </form>
                </Paper>
            </Container>

            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                }}
                open={isLoading}
            >
                <CircularProgress color="inherit" size={60} />
                <Typography variant="h6">
                    Updating Product...
                </Typography>
            </Backdrop>
        </>
    );
};

export default UpdateProductForm;
